import { DeviceUtils } from '@modules/src/ticker/createTickerMessageComponent'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { Orientation } from '@modules/src/enums/Orientation'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import { Signal } from 'typed-signals'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { GameStatesEnum } from '@/states/GameStatesEnum'
import { toMajorUnits } from '@/utils/CurrencyUnits'
import { toCurrencySymbol, formatAmount } from '@modules/src/panel/PanelUtils'
import { TickerMessageManager } from '@/managers/ui/TickerMessageManager'
import { resolvePromoWinAmount } from '@/managers/promo/PromoSessionManager'
import {
    JACKPOT_LADDER_LEVEL_NAMES,
    readLadderMilestones,
    readLadderStepPrizeText,
    readLadderTargetLevelIndex,
} from '@/gameConfigs/components/JackpotLaddersComponent'

const WINNING_COMBO_DELAY_SEC = 1
const WINNING_COMBO_RECENTER_DELAY_MS = WINNING_COMBO_DELAY_SEC * 1000 + 50
const TICKER_DEBUG = true
const HNW_RESPINS_RESET_DELAY_MS = 300
const HNW_RESPINS_RESET_COUNT = 3
const HOLD_AND_WIN_FULL_BOARD_MESSAGE_DELAY_MS = 650

export const TICKER_TEXT_NAME = 'TickerMessageText'

export interface ITickerVisualHandlers {
    scheduleTickerIdleBump: (container: any, attempt?: number) => void
    stopTickerIdleBump: (container: any) => void
    ensureTickerDecorLayers: (container: any) => void
    setTickerMessage: (container: any, message: string) => boolean
}

const logTickerDebug = (label: string, payload?: Record<string, unknown>) => {
    if (!TICKER_DEBUG) {
        return
    }

    // eslint-disable-next-line no-console
    console.log(`[TickerDebug] ${label}`, payload || {})
}

let hasSpunOnce = false
let isLineByLineActive = false
let lineByLineMessage = ''
let featureSummaryOwnsTicker = false
let fsCollectSequenceOwnsTicker = false
let ladderMilestoneOwnsTicker = false
let bigWinCommendationOwnsTicker = false
let holdAndWinFullBoardOwnsTicker = false
let isFeatureIntroActive = false
let isSplashScreenActive = true
let lastLadderTargetIndex: number | null = null
//## Index of the promo spin being played, latched on the spin action so mid-round repaints keep it.
let promoSpinsPlayedDisplay: number | null = null
let pendingWinningComboRecenterTimeout: ReturnType<typeof setTimeout> | null = null
let pendingRespinsResetTimeout: ReturnType<typeof setTimeout> | null = null
let pendingHoldAndWinCollectTickerTimeout: ReturnType<typeof setTimeout> | null = null
let pendingHoldAndWinCollectTickerTotal = 0

const resolveFirstIdleMessage = () => {
    const key = DeviceUtils.isMobileDevice() ? 'Tap SPIN to Play' : 'Click SPIN to Play'
    const translated = TranslationUtils.translate(key)

    return translated === key ? key : translated
}

const resolveRepeatIdleMessage = () => {
    const key = DeviceUtils.isMobileDevice() ? 'Tap to Play' : 'Click to Play'
    const translated = TranslationUtils.translate(key)

    return translated === key ? key : translated
}

const shouldShowPromoSpinsTicker = (): boolean => {
    const model = Facade.model
    // Runs for the whole promo session, spins left or not: the message must survive the last spin
    // and stay up until the player closes the "Congratulations" popup, which is what ends the
    // session (promoFreeSpinsSessionComplete) and hands the ticker back to the idle message.
    // Nested features (Free Spins, H&W) pause the promo counter rather than replacing it:
    // the ticker keeps reporting the frozen PFS count for the whole session.
    return Boolean(model?.isPromoSpinsRound?.())
}

/**
 * Spin index the counter has to read, from the spins the server still owes.
 * Only valid before the response of the running spin lands: from then on `spins_left` already
 * describes the next spin, which is why the value is latched on the spin action.
 */
const computePromoSpinsPlayed = (): number => {
    const promoDetails = (Facade.model?.spinData as any)?.promo_details
    const total = Math.max(0, Math.floor(Number(promoDetails?.num_spins) || 0))
    const left = Number(promoDetails?.spins_left ?? Facade.model?.spinData?.freebet?.amount_left)

    return total > 0 && Number.isFinite(left) ? Math.max(1, Math.min(total, total - left + 1)) : 1
}

/** Nested Free Spins / Hold & Win spins do not consume a promo spin: the counter stays paused. */
const latchPromoSpinsPlayed = (): void => {
    if (!shouldShowPromoSpinsTicker()) {
        promoSpinsPlayedDisplay = null
        return
    }

    if (Facade.model?.roundState?.isFreeSpins || Facade.model?.spinState?.isHoldAndWinActive) {
        return
    }

    promoSpinsPlayedDisplay = computePromoSpinsPlayed()
}

const resolvePromoSpinsTickerMessage = (): string => {
    const promoDetails = (Facade.model?.spinData as any)?.promo_details
    const total = Math.max(0, Math.floor(Number(promoDetails?.num_spins) || 0))
    const played = promoSpinsPlayedDisplay ?? computePromoSpinsPlayed()
    const winAmount = resolvePromoWinAmount(promoDetails)
    const currency = (Facade as any).model?.initData?.player?.currency ?? ''
    const currencySymbol = toCurrencySymbol(currency)
    const formattedWin = formatAmount(winAmount)
    const winText = currencySymbol ? `${currencySymbol}${formattedWin}` : formattedWin
    const fallback = `Bonus free spins : ${played}/${total}  Total win: ${winText}`
    const translated = TranslationUtils.translate('promoSpinsTickerMessage', [played, total, winText])

    return translated === 'promoSpinsTickerMessage' ? fallback : translated
}

const trySetPromoSpinsTickerMessage = (
    container: any,
    setTickerMessage: (container: any, message: string) => boolean
): boolean => {
    if (!shouldShowPromoSpinsTicker() || !container || container.destroyed) {
        return false
    }

    if (
        isLineByLineActive ||
        isTickerClaimedByFeatureMessage() ||
        holdAndWinFullBoardOwnsTicker ||
        Boolean((Facade as any).holdAndWinCollectInProgress) ||
        isFeatureIntroActive ||
        isRuntimeSplashState()
    ) {
        return false
    }

    return setTickerMessage(container, resolvePromoSpinsTickerMessage())
}

export const resolveTickerIdleMessage = () => {
    if (shouldShowPromoSpinsTicker()) {
        return resolvePromoSpinsTickerMessage()
    }

    return hasSpunOnce ? resolveRepeatIdleMessage() : resolveFirstIdleMessage()
}

const resolveWinnerMessage = () => {
    const translated = TranslationUtils.translate('Winner!')

    return translated === 'Winner!' ? 'Winner!' : translated
}

const BIG_WIN_TICKER_MESSAGES = [
    { translationKey: 'Big Win!', fallback: 'Big Win!' },
    { translationKey: 'Mega Win!', fallback: 'Mega Win!' },
    { translationKey: 'Ultra Win!', fallback: 'Ultra Win!' },
    { translationKey: 'Ultra Mega Win!', fallback: 'Ultra Mega Win!' },
] as const

const resolveBigWinCommendationMessage = (level: number): string => {
    const message = BIG_WIN_TICKER_MESSAGES[Math.max(0, Math.min(BIG_WIN_TICKER_MESSAGES.length - 1, level))]
    const translated = TranslationUtils.translate(message.translationKey)

    return translated === message.translationKey ? message.fallback : translated
}

const resolveHoldAndWinAwardedMessage = () => {
    const translated = TranslationUtils.translate('Hold and Win Awarded')

    return translated === 'Hold and Win Awarded' ? 'Hold and Win Awarded' : translated
}

const resolveFreeSpinsAwardedMessage = () => {
    const freeSpinsCount = 10
    const key = '{0} FREE SPINS AWARDED'
    const translated = TranslationUtils.translate(key, [freeSpinsCount])

    return translated === key ? `${freeSpinsCount} FREE SPINS AWARDED` : translated
}

const resolveGoldenCoinAddedMessage = () => {
    const translated = TranslationUtils.translate('Golden Coin Added!')

    return translated === 'Golden Coin Added!' ? 'Golden Coin Added!' : translated
}

const resolveHoldAndWinFullBoardMessage = () => {
    const translated = TranslationUtils.translate('holdAndWinFullBoardReachedMessage')

    return translated === 'holdAndWinFullBoardReachedMessage' ? 'Full Screen Awarded!' : translated
}

const resolveLadderMilestoneMessage = (levelIndex: number): string => {
    const jackpotName = JACKPOT_LADDER_LEVEL_NAMES[levelIndex]

    if (jackpotName) {
        const translated = TranslationUtils.translate('jackpotMilestoneReachedMessage', [jackpotName])

        return translated === 'jackpotMilestoneReachedMessage'
            ? `${jackpotName} Milestone Reached!`
            : translated
    }

    const prizeText = readLadderStepPrizeText(levelIndex)
    const translated = TranslationUtils.translate('ladderMilestoneReachedMessage', [prizeText])

    return translated === 'ladderMilestoneReachedMessage' ? `${prizeText} Milestone Reached!` : translated
}

const resolveReachedLadderLevel = (signalData?: any): number | null => {
    const milestones = readLadderMilestones(signalData)

    if (!milestones.length) {
        lastLadderTargetIndex = null
        return null
    }

    const targetIndex = readLadderTargetLevelIndex(milestones)
    const previousIndex = lastLadderTargetIndex

    lastLadderTargetIndex = targetIndex

    if (previousIndex === null || previousIndex < 0) {
        return null
    }

    if (targetIndex < 0) {
        return milestones.length - 1
    }

    return targetIndex > previousIndex ? targetIndex - 1 : null
}

const resolveGoldenCoinCollectedMessage = () => {
    const translated = TranslationUtils.translate('goldenCoinCollectedMessage')

    return translated === 'goldenCoinCollectedMessage' ? 'Golden Coin Collected!' : translated
}

const JACKPOT_POPUP_TICKER_FALLBACKS: Record<'mini' | 'minor' | 'major' | 'grand', string> = {
    mini: 'MINI JACKPOT!',
    minor: 'MINOR JACKPOT!',
    major: 'MAJOR JACKPOT!',
    grand: 'GRAND JACKPOT!',
}

const resolveJackpotPopupTickerMessage = (tier: string, amount?: string): string => {
    const key = String(tier || '').toLowerCase() as 'mini' | 'minor' | 'major' | 'grand'
    const fallback = JACKPOT_POPUP_TICKER_FALLBACKS[key]
    if (!fallback) {
        return ''
    }

    const translated = TranslationUtils.translate(fallback)
    const label = translated === fallback ? fallback : translated
    const prize = String(amount || '').trim()
    return prize ? `${label} ${prize}` : label
}

const isHoldAndWinTriggering = (): boolean => {
    const spinData = Facade.model?.spinData as any
    const nextRoundType = String(spinData?.next_round_type || '').toLowerCase()
    const respinsLeft = Number(spinData?.respins_left ?? 0)

    return (
        (nextRoundType === 'respins' || nextRoundType === 'respin') &&
        Number.isFinite(respinsLeft) &&
        respinsLeft > 0
    )
}

const resolveRetriggeredFreeSpinsCount = (): number =>
    Math.max(0, Math.floor(Number((Facade.model?.spinData as any)?.bonus?.freebet_given) || 0))

const resolveFreeSpinsRetriggerMessage = (freeSpinsCount: number) => {
    const key = '+{0} FREE SPINS AWARDED!'
    const translated = TranslationUtils.translate(key, [freeSpinsCount])

    return translated === key ? `+${freeSpinsCount} FREE SPINS AWARDED!` : translated
}

const resolveLastFreeSpinMessage = () => {
    const translated = TranslationUtils.translate('lastFreeSpinMessage')

    return translated === 'lastFreeSpinMessage' ? 'Last Spin' : translated
}

const resolveGoodLuckMessage = () => {
    const translated = TranslationUtils.translate('Good Luck!')

    return translated === 'Good Luck!' ? 'Good Luck!' : translated
}

const resolveRespinsResetMessage = (respins: number) => {
    const key = 'Re-spins Reset to {0}'
    const translated = TranslationUtils.translate(key, [respins])

    return translated === key ? `Re-spins Reset to ${respins}` : translated
}

const formatFeatureWinAmount = (winAmountInCoins: number): string => {
    const currency = (Facade as any).model?.initData?.player?.currency ?? ''

    const currencySymbol = toCurrencySymbol(currency)
    const formattedWin = formatAmount(toMajorUnits(Number(winAmountInCoins) || 0))

    return currencySymbol ? `${currencySymbol} ${formattedWin}` : formattedWin
}

/** H&W collect totals are already in display/major units (same as jackpot popup amounts). */
const formatHoldAndWinCollectAmount = (total: number): string => {
    const currency = (Facade as any).model?.initData?.player?.currency ?? ''
    const currencySymbol = toCurrencySymbol(currency)
    const normalized = Math.round((Math.max(0, Number(total) || 0) + Number.EPSILON) * 100) / 100
    return `${currencySymbol}${formatAmount(normalized)}`
}

// Distinct from the "COLLECT" gamble-button key: this is the Hold & Win collect ticker label.
const resolveHoldAndWinCollectMessage = (total: number): string => {
    const amount = formatHoldAndWinCollectAmount(total)
    const key = 'COLLECT:'
    const translated = TranslationUtils.translate(key)
    const label = translated === key ? 'COLLECT' : translated

    return `${label}: ${amount}`
}

const resolveTotalFeatureWinMessage = (winAmountInCoins: number) => {
    const amount = formatFeatureWinAmount(winAmountInCoins)
    const key = 'Total Feature Win - {0}'
    const translated = TranslationUtils.translate(key, [amount])

    return translated === key ? `Total Feature Win - ${amount}` : translated
}

const isTickerClaimedByFeatureMessage = (): boolean =>
    bigWinCommendationOwnsTicker || ladderMilestoneOwnsTicker || featureSummaryOwnsTicker || fsCollectSequenceOwnsTicker

const isRuntimeSplashState = (): boolean => {
    const current = String((Facade as any).currentState || '')
    return (
        current === GameStatesEnum.GET_INIT_RESPONSE_STATE ||
        current === GameStatesEnum.BEFORE_LOADING_STATE ||
        current === GameStatesEnum.LOADING_STATE ||
        current === GameStatesEnum.INIT_STATE ||
        current === GameStatesEnum.DISPLAY_SPLASH_SCREEN_STATE
    )
}

const clearPendingHoldAndWinCollectTicker = () => {
    if (!pendingHoldAndWinCollectTickerTimeout) {
        return
    }

    clearTimeout(pendingHoldAndWinCollectTickerTimeout)
    pendingHoldAndWinCollectTickerTimeout = null
}

const clearPendingWinningComboRecenter = () => {
    if (!pendingWinningComboRecenterTimeout) {
        return
    }

    clearTimeout(pendingWinningComboRecenterTimeout)
    pendingWinningComboRecenterTimeout = null
}

const clearPendingRespinsReset = () => {
    if (!pendingRespinsResetTimeout) {
        return
    }

    clearTimeout(pendingRespinsResetTimeout)
    pendingRespinsResetTimeout = null
}

const scheduleWinningComboRecenter = (container: any, ensureTickerDecorLayers: (container: any) => void) => {
    clearPendingWinningComboRecenter()

    logTickerDebug('scheduleWinningComboRecenter', {
        delayMs: WINNING_COMBO_RECENTER_DELAY_MS,
    })

    pendingWinningComboRecenterTimeout = setTimeout(() => {
        pendingWinningComboRecenterTimeout = null

        if (isFeatureIntroActive || !container || container.destroyed) {
            logTickerDebug('skipWinningComboRecenter', {
                isFeatureIntroActive,
                hasContainer: !!container,
                destroyed: !!container?.destroyed,
            })
            return
        }

        logTickerDebug('runWinningComboRecenter')
        ensureTickerDecorLayers(container)
    }, WINNING_COMBO_RECENTER_DELAY_MS)
}

export const TICKER_PARKED_TOTAL_WIN_SIGNAL = new Signal<(data: any) => void>()
export const TICKER_PARKED_SPIN_START_SIGNAL = new Signal<(data: any) => void>()

export const createTickerMessageEvents = (visualHandlers: ITickerVisualHandlers) => {
    const { scheduleTickerIdleBump, stopTickerIdleBump, ensureTickerDecorLayers, setTickerMessage } = visualHandlers
    let currentBigWinCommendationLevel = 0

    //# The ticker's "a spin is starting" text. Shared by startSpin and the free spins intro.
    const setSpinStartTickerMessage = (container: any): void => {
        clearPendingWinningComboRecenter()
        clearPendingRespinsReset()

        const isFreeSpinsRound = Boolean(Facade.model?.roundState?.isFreeSpins)
        const spinsLeft = Number((Facade.model?.spinData as any)?.freebet?.amount_left ?? -1)
        const isLastFreeSpinStarting = isFreeSpinsRound && Number.isFinite(spinsLeft) && spinsLeft === 1

        // "Last Spin" still wins on the closing free spin; every other spin of a PFS session
        // (base or nested feature) reports the paused promo count.
        if (!isLastFreeSpinStarting && trySetPromoSpinsTickerMessage(container, setTickerMessage)) {
            return
        }

        setTickerMessage(container, isLastFreeSpinStarting ? resolveLastFreeSpinMessage() : resolveGoodLuckMessage())
    }

    return [
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
        callback: () => {
            if (!Boolean(Facade.model?.isFreeSpinsRestore)) {
                lastLadderTargetIndex = null
            }
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).displaySplashScreenState],
        callback: (data: any) => {
            isSplashScreenActive = true
            clearPendingWinningComboRecenter()
            stopTickerIdleBump(data.component.componentElement)
            data.component.componentElement.visible = false
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
        callback: (data: any) => {
            logTickerDebug('featureIntroRequested')
            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            isFeatureIntroActive = true
            stopTickerIdleBump(data.component.componentElement)
            data.component.componentElement.visible = false
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).featureIntroContinue],
        callback: (data: any) => {
            logTickerDebug('featureIntroContinue')
            isFeatureIntroActive = false
            data.component.componentElement.visible = true
            stopTickerIdleBump(data.component.componentElement)
            ensureTickerDecorLayers(data.component.componentElement)

            //## Change Ticker Message from "N FREE SPINS AWARDED" to Next State.
            const isFreeSpinsIntro =
                Boolean(Facade.model?.roundState?.isFreeSpins) && !Boolean(Facade.model?.spinState?.isHoldAndWinActive)

            if (isFreeSpinsIntro) {
                setSpinStartTickerMessage(data.component.componentElement)
            }
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [
            GameSignals.completeSplashScreenState,
            GameSignals.showBaseGameBackground,
            GameSignals.idle,
            GameSignals.startSpin,
            ModuleSignals.gameResized,

            //## Show the Ticker in Unifinished case..! 
            (GameSignals as any).unfinishedRoundRequested,
        ],
        callback: (data: any) => {
            logTickerDebug('tickerMainRecenterEvent')
            clearPendingWinningComboRecenter()

            isSplashScreenActive = isRuntimeSplashState()
            clearPendingRespinsReset()

            if (isSplashScreenActive || isFeatureIntroActive) {
                data.component.componentElement.visible = false
                return
            }

            data.component.componentElement.visible = true
            stopTickerIdleBump(data.component.componentElement)
            ensureTickerDecorLayers(data.component.componentElement)

            if (isLineByLineActive || isTickerClaimedByFeatureMessage()) {
                TickerMessageManager.reassertLineByLineMessage({
                    container: data.component.componentElement,
                    isLineByLineActive,
                    lineByLineMessage,
                    isTickerClaimedByFeatureMessage,
                    setTickerMessage,
                })
                return
            }

            if (trySetPromoSpinsTickerMessage(data.component.componentElement, setTickerMessage)) {
                scheduleTickerIdleBump(data.component.componentElement)
                return
            }

            scheduleTickerIdleBump(data.component.componentElement)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.showRegularWin],
        callback: (data: any) => {
            logTickerDebug('showRegularWin', {
                signalData: data?.signalData,
            })
            isSplashScreenActive = isRuntimeSplashState()

            if (isSplashScreenActive || isFeatureIntroActive) {
                data.component.componentElement.visible = false
                return
            }

            data.component.componentElement.visible = true

            stopTickerIdleBump(data.component.componentElement)
            ensureTickerDecorLayers(data.component.componentElement)
            scheduleWinningComboRecenter(data.component.componentElement, ensureTickerDecorLayers)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.bigWinPresentation],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            const highestLevel = Math.max(0, Math.floor(Number(data?.signalData?.bigWinLevel) || 0))
            currentBigWinCommendationLevel = data?.signalData?.highestOnly ? highestLevel : 0
            bigWinCommendationOwnsTicker = true

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            stopTickerIdleBump(container)
            container.visible = true
            setTickerMessage(container, resolveBigWinCommendationMessage(currentBigWinCommendationLevel))
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.startBigWinLevel],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed || !bigWinCommendationOwnsTicker) {
                return
            }

            currentBigWinCommendationLevel = Math.min(
                BIG_WIN_TICKER_MESSAGES.length - 1,
                currentBigWinCommendationLevel + 1
            )
            setTickerMessage(container, resolveBigWinCommendationMessage(currentBigWinCommendationLevel))
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.showBigWinComplete],
        callback: () => {
            bigWinCommendationOwnsTicker = false
            currentBigWinCommendationLevel = 0
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).holdAndWinCollectProgress],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            isSplashScreenActive = isRuntimeSplashState()

            if (isSplashScreenActive || isFeatureIntroActive) {
                container.visible = false
                return
            }

            const total = Math.max(0, Number(data?.signalData) || 0)
            // Preserve decimals from H&W coin values (do not floor).
            const displayTotal =
                Math.round((total + Number.EPSILON) * 100) / 100
            logTickerDebug('holdAndWinCollectProgress', { total: displayTotal })

            clearPendingWinningComboRecenter()

            if (holdAndWinFullBoardOwnsTicker) {
                pendingHoldAndWinCollectTickerTotal = displayTotal

                if (!pendingHoldAndWinCollectTickerTimeout) {
                    pendingHoldAndWinCollectTickerTimeout = setTimeout(() => {
                        pendingHoldAndWinCollectTickerTimeout = null
                        holdAndWinFullBoardOwnsTicker = false

                        if (!container || container.destroyed || isFeatureIntroActive) {
                            return
                        }

                        setTickerMessage(
                            container,
                            resolveHoldAndWinCollectMessage(pendingHoldAndWinCollectTickerTotal)
                        )
                    }, HOLD_AND_WIN_FULL_BOARD_MESSAGE_DELAY_MS)
                }

                return
            }

            setTickerMessage(container, resolveHoldAndWinCollectMessage(displayTotal))
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).holdAndWinFullBoardReached],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            clearPendingHoldAndWinCollectTicker()
            holdAndWinFullBoardOwnsTicker = true
            setTickerMessage(container, resolveHoldAndWinFullBoardMessage())
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [
            (GameSignals as any).holdAndWinFullBoardCleared,
            (GameSignals as any).holdAndWinCollectComplete,
            (GameSignals as any).holdAndWinStop,
        ],
        callback: () => {
            clearPendingHoldAndWinCollectTicker()
            holdAndWinFullBoardOwnsTicker = false
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.showWinLabels],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            if (bigWinCommendationOwnsTicker) {
                return
            }

            setTickerMessage(container, resolveWinnerMessage())
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).holdAndWinTriggerCoinWinStart],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            setTickerMessage(container, resolveHoldAndWinAwardedMessage())
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).jackpotPopupTierShown],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            const payload = data?.signalData
            const tier =
                typeof payload === 'string'
                    ? payload
                    : String(payload?.tier || '')
            const amount = typeof payload === 'object' && payload ? String(payload.amount || '') : ''
            const message = resolveJackpotPopupTickerMessage(tier, amount)
            if (!message) {
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            clearPendingHoldAndWinCollectTicker()
            holdAndWinFullBoardOwnsTicker = false
            container.visible = true
            setTickerMessage(container, message)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.activateScatters],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            if (isHoldAndWinTriggering()) {
                logTickerDebug('activateScatters:skippedForHoldAndWin')
                return
            }

            const isRetrigger = Boolean(Facade.model?.roundState?.isFreeSpins)

            if (!isRetrigger) {
                setTickerMessage(container, resolveFreeSpinsAwardedMessage())
                return
            }

            const freeSpinsCount = resolveRetriggeredFreeSpinsCount()
            if (freeSpinsCount <= 0) {
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            setTickerMessage(container, resolveFreeSpinsRetriggerMessage(freeSpinsCount))
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [
            (GameSignals as any).fsCollectSequenceRequested,
            (GameSignals as any).fsCollectSequenceComplete,
        ],
        callback: () => {
            ladderMilestoneOwnsTicker = false
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).fsCollectSequenceRequested],
        callback: () => {
            fsCollectSequenceOwnsTicker = true
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).fsCollectSequenceComplete],
        callback: () => {
            fsCollectSequenceOwnsTicker = false
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.updateJarValue, GameSignals.multiplierLanded],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            const reachedLevelIndex = resolveReachedLadderLevel(data?.signalData)

            if (reachedLevelIndex === null) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            const message = resolveLadderMilestoneMessage(reachedLevelIndex)

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            ladderMilestoneOwnsTicker = true
            setTickerMessage(container, message)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.fsTopMultiplierCollectFly],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            if (ladderMilestoneOwnsTicker) {
                return
            }

            const message = resolveGoldenCoinCollectedMessage()
            const textChild = container.getChildByName(TICKER_TEXT_NAME) as any
            const currentText = typeof textChild?.text === 'string' ? textChild.text.trim() : ''

            if (currentText === message) {
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            setTickerMessage(container, message)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).holdAndWinStickyStopped],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            if (holdAndWinFullBoardOwnsTicker) {
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            setTickerMessage(container, resolveGoldenCoinAddedMessage())

            pendingRespinsResetTimeout = setTimeout(() => {
                pendingRespinsResetTimeout = null

                if (isFeatureIntroActive || !container || container.destroyed) {
                    return
                }

                setTickerMessage(container, resolveRespinsResetMessage(HNW_RESPINS_RESET_COUNT))
            }, HNW_RESPINS_RESET_DELAY_MS)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.showFreeSpinsSummary],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive) {
                container.visible = false
                return
            }

            const winAmountInCoins = Number(data?.signalData ?? 0)
            const message = resolveTotalFeatureWinMessage(winAmountInCoins)

            featureSummaryOwnsTicker = true

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            setTickerMessage(container, message)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).lineByLineStepChanged],
        callback: (data: any) => {
            const container = data.component.componentElement
            if (!container || container.destroyed) {
                return
            }

            isSplashScreenActive = isRuntimeSplashState()

            if (isSplashScreenActive || isFeatureIntroActive) {
                container.visible = false
                return
            }

            const message = TickerMessageManager.resolveLinePaysMessage(data?.signalData?.combination)
            if (!message) {
                return
            }

            isLineByLineActive = true
            lineByLineMessage = message

            if (isTickerClaimedByFeatureMessage()) {
                return
            }

            clearPendingWinningComboRecenter()
            clearPendingRespinsReset()
            setTickerMessage(container, message)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [(GameSignals as any).lineByLineLoopStopped],
        callback: (data: any) => {
            const container = data.component.componentElement
            const previousMessage = lineByLineMessage

            isLineByLineActive = false
            lineByLineMessage = ''

            if (!container || container.destroyed || !previousMessage) {
                return
            }

            queueMicrotask(() => {
                if (isLineByLineActive || container.destroyed) {
                    return
                }

                const textChild = container.getChildByName(TICKER_TEXT_NAME) as any
                const currentText = typeof textChild?.text === 'string' ? textChild.text.trim() : ''

                if (currentText !== previousMessage) {
                    return
                }

                setTickerMessage(container, resolveTickerIdleMessage())
                scheduleTickerIdleBump(container)
            })
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [GameSignals.startSpin],
        callback: (data: any) => {
            hasSpunOnce = true
            featureSummaryOwnsTicker = false
            fsCollectSequenceOwnsTicker = false
            latchPromoSpinsPlayed()

            const container = data?.component?.componentElement
            if (!container || container.destroyed) {
                return
            }

            if (isFeatureIntroActive || isRuntimeSplashState()) {
                return
            }

            setSpinStartTickerMessage(container)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [
            (GameSignals as any).promoFreeSpinsAccepted,
            (GameSignals as any).setWinPanelValue,
            (GameSignals as any).holdAndWinStartSpin,
            (GameSignals as any).holdAndWinStop,
            // Same treatment for a Free Spins feature opened inside PFS: repaint the paused
            // count as soon as the feature starts, ends, or its intro popup closes.
            (GameSignals as any).showFreeSpinsBackground,
            (GameSignals as any).featureIntroContinue,
            GameSignals.bonusStopComplete,
        ],
        callback: (data: any) => {
            const container = data?.component?.componentElement
            if (!container || container.destroyed) {
                return
            }

            trySetPromoSpinsTickerMessage(container, setTickerMessage)
        },
    },
    {
        event: ComponentEvent.CUSTOM_NEW,
        onGameSignalsTriggered: [
            (GameSignals as any).promoFreeSpinsSessionComplete,
            (GameSignals as any).promoFreeSpinsDeclined,
        ],
        callback: (data: any) => {
            promoSpinsPlayedDisplay = null

            const container = data?.component?.componentElement
            if (!container || container.destroyed || isFeatureIntroActive || isRuntimeSplashState()) {
                return
            }

            // Plain idle text, not resolveTickerIdleMessage(): the promo session may not be closed
            // yet on this listener, and re-reading it here would print the promo message again.
            setTickerMessage(container, hasSpunOnce ? resolveRepeatIdleMessage() : resolveFirstIdleMessage())
            scheduleTickerIdleBump(container)
        },
    },
    ]
}

export const tickerMessagePosition = [
    { orientation: Orientation.LANDSCAPE, x: 905, y: 772.5 },
    { orientation: Orientation.PORTRAIT, x: 396.5, y: 1290 },
]

export const tickerMessageTextPosition = [
    { orientation: Orientation.LANDSCAPE, x: 147.5, y: 28 },
    { orientation: Orientation.PORTRAIT, x: 147.5, y: 28 },
]
